import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, AlertCircle, CheckCircle, Copy, Download, Key } from 'lucide-react';
import { appDeviceService } from '../services';
import type { RegisterDeviceResponse } from '../services';
import { useToast } from '../components/common/ToastContainer';

// Validate WireGuard public key format (base64-encoded 32 bytes = 44 chars with padding)
const isValidWireGuardPublicKey = (key: string): boolean => {
  if (!key) return false;
  // WireGuard public keys are 32 bytes, base64-encoded = 44 characters (including '=' padding)
  if (key.length !== 44) return false;
  // Must end with '=' (base64 padding for 32 bytes)
  if (!key.endsWith('=')) return false;
  // Valid base64 characters
  const base64Regex = /^[A-Za-z0-9+/]+=$/;
  return base64Regex.test(key);
};

const AppDeviceRegister = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegisterDeviceResponse | null>(null);
  const [publicKeyError, setPublicKeyError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    deviceName: '',
    platform: 'iOS' as 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux',
    osVersion: '',
    appVersion: '',
    publicKey: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'publicKey') {
      setPublicKeyError(null);
    }
  };

  const validatePublicKey = (): boolean => {
    if (!formData.publicKey.trim()) {
      setPublicKeyError('WireGuard public key is required');
      return false;
    }
    if (!isValidWireGuardPublicKey(formData.publicKey.trim())) {
      setPublicKeyError('Invalid WireGuard public key format. Must be 44 characters (base64-encoded 32 bytes)');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.deviceName.trim()) {
      toast.error('Validation Error', 'Device name is required');
      return;
    }

    if (!validatePublicKey()) {
      return;
    }

    try {
      setLoading(true);

      // Generate device fingerprint (in production, this should be the actual device fingerprint)
      const deviceFingerprint = `${formData.platform}-${Date.now()}-${Math.random().toString(36).substring(7)}`;

      const response = await appDeviceService.register({
        deviceName: formData.deviceName,
        deviceFingerprint,
        platform: formData.platform,
        osVersion: formData.osVersion || undefined,
        appVersion: formData.appVersion || undefined,
        publicKey: formData.publicKey.trim(),
      });

      setRegistrationData(response);
      setStep('success');
      toast.success('Device Registered', 'Your device has been registered successfully');
    } catch (err: any) {
      toast.error('Registration Failed', err.message || 'Failed to register device');
    } finally {
      setLoading(false);
    }
  };

  // Generate WireGuard config without the private key (user must add their own)
  const generateWireGuardConfig = (): string => {
    if (!registrationData) return '';

    const { peer } = registrationData;
    return `[Interface]
# Add your private key here (the one that corresponds to the public key you registered)
PrivateKey = YOUR_PRIVATE_KEY_HERE
Address = ${peer.assignedIp}
DNS = 1.1.1.1, 8.8.8.8

[Peer]
PublicKey = ${peer.serverPublicKey}
Endpoint = ${peer.endpointHost || 'vpn.example.com'}:${peer.listenPort}
AllowedIPs = ${peer.allowedIps.join(', ')}
PersistentKeepalive = ${peer.persistentKeepalive}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied', 'Configuration copied to clipboard');
  };

  const downloadConfig = () => {
    if (!registrationData) return;
    const config = generateWireGuardConfig();
    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.deviceName}.conf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded', 'Configuration file downloaded');
  };

  if (step === 'success' && registrationData) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app-devices')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Device Registered Successfully</h1>
            <p className="text-sm text-gray-500">Configure your WireGuard VPN client with the details below</p>
          </div>
        </div>

        {/* Success Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-green-900">Registration Complete</h3>
              <p className="mt-1 text-sm text-green-700">
                Your device "{formData.deviceName}" has been registered and assigned VPN IP {registrationData.peer.assignedIp}
              </p>
            </div>
          </div>
        </div>

        {/* Important Note about Private Key */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Key className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-900">Important: Add Your Private Key</h3>
              <p className="mt-1 text-sm text-amber-700">
                The configuration below requires your private key. Replace <code className="bg-amber-100 px-1 rounded">YOUR_PRIVATE_KEY_HERE</code> with
                the private key you generated on your device (the one that corresponds to the public key you registered).
              </p>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">WireGuard Configuration</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(generateWireGuardConfig())}
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </button>
              <button
                onClick={downloadConfig}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </div>
          </div>
          <div className="p-6">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
              {generateWireGuardConfig()}
            </pre>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Setup Instructions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">For Mobile Devices (iOS/Android):</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Install the WireGuard app from the App Store or Google Play</li>
                <li>Open the app and tap "Add a tunnel"</li>
                <li>Choose "Create from file or archive" or paste the configuration</li>
                <li><strong>Replace YOUR_PRIVATE_KEY_HERE with your device's private key</strong></li>
                <li>Activate the VPN tunnel</li>
              </ol>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">For Desktop (Windows/macOS/Linux):</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Download and install WireGuard from wireguard.com</li>
                <li>Click "Import tunnel(s) from file" and select the downloaded .conf file</li>
                <li><strong>Edit the tunnel and replace YOUR_PRIVATE_KEY_HERE with your private key</strong></li>
                <li>Click "Activate" to connect</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button onClick={() => navigate('/app-devices')} className="btn-primary">
            Go to App Devices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/app-devices')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Register VPN Device</h1>
          <p className="text-sm text-gray-500">Add a mobile or desktop device to connect via VPN</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">Before You Begin</h3>
            <p className="mt-1 text-sm text-blue-700">
              You need to generate a WireGuard key pair on your device first. The public key will be used for registration.
              Keep your private key secure - you'll need it to configure your VPN client.
            </p>
            <div className="mt-2 p-2 bg-blue-100 rounded font-mono text-xs text-blue-800">
              <p className="font-semibold">Generate keys on your device:</p>
              <p className="mt-1">wg genkey | tee privatekey | wg pubkey &gt; publickey</p>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Device Information</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.deviceName}
              onChange={(e) => handleChange('deviceName', e.target.value)}
              placeholder="My iPhone"
              className="input w-full"
              required
            />
            <p className="mt-1 text-xs text-gray-500">A friendly name to identify this device</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WireGuard Public Key <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.publicKey}
              onChange={(e) => handleChange('publicKey', e.target.value)}
              placeholder="e.g., uJv5tlYip93OjvI3pzx8C7kXXfL8+RsscPrL5TwAUgI="
              className={`input w-full font-mono ${publicKeyError ? 'border-red-500' : ''}`}
              required
            />
            {publicKeyError ? (
              <p className="mt-1 text-xs text-red-600">{publicKeyError}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Enter your device's public key (44 characters). Generate it with: <code className="bg-gray-100 px-1 rounded">wg genkey | wg pubkey</code>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.platform}
              onChange={(e) => handleChange('platform', e.target.value)}
              className="input w-full"
              required
            >
              <option value="iOS">iOS</option>
              <option value="Android">Android</option>
              <option value="Windows">Windows</option>
              <option value="macOS">macOS</option>
              <option value="Linux">Linux</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">OS Version (Optional)</label>
              <input
                type="text"
                value={formData.osVersion}
                onChange={(e) => handleChange('osVersion', e.target.value)}
                placeholder="e.g., iOS 17.0"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">App Version (Optional)</label>
              <input
                type="text"
                value={formData.appVersion}
                onChange={(e) => handleChange('appVersion', e.target.value)}
                placeholder="e.g., 1.0.0"
                className="input w-full"
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => navigate('/app-devices')} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary inline-flex items-center space-x-2">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Registering...</span>
                </>
              ) : (
                <>
                  <Smartphone className="w-4 h-4" />
                  <span>Register Device</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppDeviceRegister;
